/**
 * The regions of a record surface — header content, header actions, body,
 * footer — each a thin `switch` over `RecordSurfaceContext`'s state.
 *
 * Hosts place these in their chrome's slots: `SlideoutMenu`'s `header` /
 * `headerActions` / `footer` / children, or `SlideoutMenu.Header` +
 * `.Content` composed inline. The chrome renders the slots — and its close
 * button at the end of the header row — in EVERY state; this file only
 * decides what fills them, so no resolution state can remove the exit
 * (docs/claude/REACT_19_PATTERNS.md § 1).
 *
 * Each switch is exhaustive: a new `RecordSurfaceState` member fails to
 * compile here rather than falling through silently in one region.
 */

import { type ReactElement, type ReactNode } from 'react';

import { SurfaceBackButton } from '@/lib/base/overlays/surface-back-button';
import { UnsavedChangesModal } from '@/lib/base/overlays/unsaved-changes-modal';
import { QueryErrorState } from '@/lib/components';

import {
  type RecordSurfaceState,
  useRecordSurface,
} from './contexts/RecordSurfaceContext';
import { useRecordView } from './contexts/RecordViewContext';
import {
  RecordHeaderActions,
  RecordHeaderContent,
  RecordHeaderIdentity,
} from './record-header';
import { RecordBodySkeleton, RecordHeaderSkeleton } from './record-skeleton';
import { RecordAccessDeniedNotice } from './RecordAccessDeniedNotice';
import { RecordBody } from './RecordBody';
import { AutomationRunFooter } from './sections/automation-run-footer';
import { EmailThreadReplyFooter } from './sections/email-thread-reply-footer';
import { TaskActionFooter } from './task/TaskActionFooter';

function assertNever(state: never): never {
  throw new Error(`Unhandled record surface state: ${JSON.stringify(state)}`);
}

/**
 * Header content when there is no record to describe (error / denied): the
 * back chevron, if the host stacks records, in a row the same height as the
 * loaded header's chip row so the chrome's close does not jump between
 * states.
 */
function BareHeaderContent(): ReactElement {
  return (
    <div className='flex min-h-9 items-center'>
      <SurfaceBackButton />
    </div>
  );
}

export function RecordSlideoutHeaderContent(): ReactElement {
  const { state } = useRecordSurface();
  switch (state.status) {
    case 'ready':
      return <RecordHeaderContent />;
    case 'loading':
      return <RecordHeaderSkeleton />;
    case 'error':
    case 'denied':
      return <BareHeaderContent />;
    default:
      return assertNever(state);
  }
}

export function RecordSlideoutHeaderActions(): ReactElement | null {
  const { state } = useRecordSurface();
  switch (state.status) {
    case 'ready':
      return <RecordHeaderActions />;
    case 'loading':
    case 'error':
    case 'denied':
      return null;
    default:
      return assertNever(state);
  }
}

/**
 * The identity block, rendered in the header's second row. `loading` returns
 * null here (not a skeleton): `RecordHeaderSkeleton` already renders the full
 * chip+identity skeleton in the FIRST-row slot, and with no actions cluster
 * to share that row with, its left column already spans full width — so the
 * loading geometry already matches the ready two-row layout without a
 * second-row skeleton.
 */
export function RecordSlideoutHeaderIdentity(): ReactElement | null {
  const { state } = useRecordSurface();
  switch (state.status) {
    case 'ready':
      return <RecordHeaderIdentity />;
    case 'loading':
    case 'error':
    case 'denied':
      return null;
    default:
      return assertNever(state);
  }
}

function bodyFor(state: RecordSurfaceState): ReactNode {
  switch (state.status) {
    case 'ready':
      return <RecordBody />;
    case 'loading':
      return <RecordBodySkeleton />;
    case 'error':
      return (
        <div className='p-6'>
          <QueryErrorState error={state.error} label='Unable to load record' />
        </div>
      );
    case 'denied':
      return <RecordAccessDeniedNotice statusCode={state.statusCode} />;
    default:
      return assertNever(state);
  }
}

/**
 * The scroll region's content. The unsaved-changes modal is rendered here —
 * inside the surface's stacking layer — so it always stacks above the panel
 * that owns it.
 */
export function RecordSlideoutBody(): ReactElement {
  const { state, unsavedChangesModalProps } = useRecordSurface();
  return (
    <>
      {/* Legacy handle kept for e2e/contract tests that address the record
          surface's content region. */}
      <div className='w-full' data-testid='crm-object-slideout'>
        {bodyFor(state)}
      </div>
      <UnsavedChangesModal {...unsavedChangesModalProps} />
    </>
  );
}

/**
 * Sticky footers a loaded record may carry. All three self-gate to null for
 * every other entity type / kind, and are mutually exclusive (no object is
 * an email thread, a task note, and a scheduled automation at once), so at
 * most one is ever visible. Rendering them here — the chrome's pinned footer
 * slot — rather than inside RecordBody's scrollable content is what keeps
 * them actually sticky.
 */
function ReadyFooter(): ReactElement {
  const { object } = useRecordView();
  return (
    <>
      <EmailThreadReplyFooter object={object} />
      <TaskActionFooter object={object} />
      <AutomationRunFooter object={object} />
    </>
  );
}

export function RecordSlideoutFooter(): ReactElement | null {
  const { state } = useRecordSurface();
  switch (state.status) {
    case 'ready':
      return <ReadyFooter />;
    case 'loading':
    case 'error':
    case 'denied':
      return null;
    default:
      return assertNever(state);
  }
}
